import type { TransformDateProperties } from '../commons/fe-commons';
import type {
    CenturyPaginatorSimple,
    ConservTaskInfoRaw,
    NamedCenturyObject,
    ProxyType,
    QueueInfoRaw,
    SchemaMsdInfo,
    SchemeFilterRaw,
    SchemeInfoRaw,
    SchemeVersionInfoRaw,
    StaticMeta,
    VolumeFilterRaw,
    VolumeInfoRaw,
} from './century-shared';

export const CENTURY_SOURCE = {
    SRC_FATTURA_ATTIVA: 'FA1',
    SRC_FATTURA_PASSIVA: 'FP1',
    SRC_LIBRI: 'CL1',
    SRC_GENERALI: 'GEN',
    SRC_SYSTEM: 'SYS',
    SRC_SYSTEM_MDC: 'SMC',
} as const;
export type CenturySource = typeof CENTURY_SOURCE[keyof typeof CENTURY_SOURCE];

export type ObjectType = 'ConservTask' | 'Queue' | 'Scheme' | 'SchemeMsd' | 'SchemeMappingInfo' | 'Volume';
export type MethodType = 'list' | 'get' | 'get2';

export type CenturyCommonRequest<O extends ObjectType> = {
    type: O;
    group?: string | null;
    owner?: string;
    proxyType?: ProxyType;
};

export type CenturyListRequest<O extends ObjectType, F> = CenturyCommonRequest<O> & {
    paginator?: CenturyPaginatorSimple;
    filter?: F;
};

export type CenturyListResponse<T> = {
    total: number | string;
    list: T[];
};

export type CenturyGetRequest<O extends ObjectType> = CenturyCommonRequest<O> & {
    profile: string;
    name?: string;
};

export type CenturyGet2Request<O extends ObjectType> = CenturyCommonRequest<O> & {
    id: number | string;
};

export type CommonCenturyObject = NamedCenturyObject & {
    staticMeta: StaticMeta;
    staticMetaId?: number | string;
    staticMetaChanged?: number | string | null | undefined;
    source: CenturySource;
};

export type ConservTaskInfo = TransformDateProperties<Omit<ConservTaskInfoRaw, 'staticMeta' | 'source'>> & CommonCenturyObject;

export type QueueInfo = TransformDateProperties<Omit<QueueInfoRaw, 'staticMeta' | 'source'>> & CommonCenturyObject;

export type VolumeInfo = TransformDateProperties<Omit<VolumeInfoRaw, 'staticMeta' | 'source'>>
& CommonCenturyObject;

export type SchemeInfo = TransformDateProperties<Omit<SchemeInfoRaw, 'staticMeta' | 'source'>> & CommonCenturyObject;

export type SchemeVersionInfo = Omit<SchemeVersionInfoRaw, 'scheme'> & {
    schemaMsd?: SchemaMsdInfo[];
    scheme: SchemeInfo;
};

export type CenturyListApi = {
    'ConservTask': {
        request: CenturyListRequest<'ConservTask', undefined>;
        response: CenturyListResponse<ConservTaskInfo>;
    };
    'Queue': {
        request: CenturyListRequest<'Queue', undefined>;
        response: CenturyListResponse<QueueInfo>;
    };
    'Volume': {
        request: CenturyListRequest<'Volume', TransformDateProperties<VolumeFilterRaw>>;
        response: CenturyListResponse<VolumeInfo>;
    };
    'Scheme': {
        request: CenturyListRequest<'Scheme', TransformDateProperties<SchemeFilterRaw>>;
        response: CenturyListResponse<SchemeInfo>;
    };
};

export type CenturyGetApi = {
    'Volume': {
        request: CenturyGetRequest<'Volume'>;
        response: VolumeInfo;
    };
    'Scheme': {
        request: CenturyGetRequest<'Scheme'>;
        response: SchemeInfo;
    };
    'SchemeMsd': {
        request: CenturyGetRequest<'SchemeMsd'> & {
            options?: {
                parseSchemaMsd?: boolean;
            };
        };
        response: {
            schemaMsd: SchemaMsdInfo[];
        } | string;
    };
};

export type CenturyGet2Api = {
    'Volume': {
        request: CenturyGet2Request<'Volume'>;
        response: VolumeInfo;
    };
    'Scheme': {
        request: CenturyGet2Request<'Scheme'>;
        response: SchemeInfo;
    };
    'SchemeMsd': {
        request: CenturyGet2Request<'SchemeMsd'> & {
            options?: {
                parseSchemaMsd?: boolean;
            };
        };
        response: {
            schemaMsd: SchemaMsdInfo[];
        } | string;
    };
};

/* eslint-disable @stylistic/indent */
export type CenturyRequest<T extends MethodType, O extends ObjectType> =
    T extends 'list' ? (O extends keyof CenturyListApi ? CenturyListApi[O]['request'] : never) :
    T extends 'get' ? (O extends keyof CenturyGetApi ? CenturyGetApi[O]['request'] : never) :
    T extends 'get2' ? (O extends keyof CenturyGet2Api ? CenturyGet2Api[O]['request'] : never) :
    never;

export type CenturyResponse<T extends MethodType, O extends ObjectType> =
    T extends 'list' ? (O extends keyof CenturyListApi ? CenturyListApi[O]['response'] : never) :
    T extends 'get' ? (O extends keyof CenturyGetApi ? CenturyGetApi[O]['response'] : never) :
    T extends 'get2' ? (O extends keyof CenturyGet2Api ? CenturyGet2Api[O]['response'] : never) :
    never;
/* eslint-enable @stylistic/indent */

export type CenturyCallback<T extends MethodType, O extends ObjectType> = (result: CenturyResponse<T, O>) => void;

export type TypedCenturyPromise = <T extends MethodType, O extends ObjectType>(
    command: T,
    query?: CenturyRequest<T, O>,
) => Promise<CenturyResponse<T, O>>;

export type TypedCenturyCallback = <T extends MethodType, O extends ObjectType>(
    command: T,
    query?: CenturyRequest<T, O>,
    callback?: CenturyCallback<T, O>
) => void;

export type TypedCenturyCall = TypedCenturyPromise & TypedCenturyCallback;

